<template>
    <BulkActionBar
        :items="items"
        :is-mobile="$vuetify.breakpoint.smAndDown"
        :selected-list-ids="selectedListIds"
        :items-text="$t('tasks')"
        :items-errors-text="$t('task(s) had errors')"
        :items-selected-text="$t('tasks selected')"
        :relative-elem="relativeElem"
        :offset-y="isCalendarOpen ? 563 : 9"
        :is-sticky="isCalendarOpen"
        :bulk-action-errors-total="bulkActionErrorsTotal"
        @bulkSelection="showSelectionCheckbox = $event"
    >
        <template v-slot:actions>
            <DelegateMenu
                v-slot="{ on: menuOn }"
                :tasks="items"
            >
                <v-tooltip bottom>
                    <template v-slot:activator="{ on: tooltipOn }">
                        <div class="mx-1">
                            <icon-button
                                color="white"
                                icon="ScanFace"
                                :on="{...tooltipOn, ...menuOn}"
                            />
                        </div>
                    </template>
                    {{ $t("Delegate to...") }}
                </v-tooltip>
            </DelegateMenu>
            <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                    <div class="mx-1">
                        <icon-button
                            :on="on"
                            color="white"
                            icon="BookmarkPlus"
                            @click="onExtraWork"
                        />
                    </div>
                </template>
                {{ $t("Extra work") }}
            </v-tooltip>
            <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                    <div class="mx-1">
                        <icon-button
                            :on="on"
                            color="white"
                            icon="Flag"
                            @click="onSetImportant"
                        />
                    </div>
                </template>
                {{ $t("Important") }}
            </v-tooltip>
            <BulkTaskSnoozeMenu v-slot="{ on: menuOn }">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on: tooltipOn }">
                        <div class="mx-1">
                            <icon-button
                                :on="{...menuOn, ...tooltipOn}"
                                color="white"
                                icon="CalendarClock"
                            />
                        </div>
                    </template>
                    {{ $t("Postpone") }}
                </v-tooltip>
            </BulkTaskSnoozeMenu>
            <SplitTimeMenu
                v-slot="{ on: menuOn }"
                :is-mobile="$vuetify.breakpoint.smAndDown"
                :is-billable="splitTimeMenuIsBillable"
            >
                <v-tooltip bottom>
                    <template v-slot:activator="{ on: tooltipOn }">
                        <div class="mx-1">
                            <icon-button
                                icon="PlayCircle"
                                color="white"
                                :on="{...menuOn, ...tooltipOn}"
                            />
                        </div>
                    </template>
                    {{ $t("Add time") }}
                </v-tooltip>
            </SplitTimeMenu>
            <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                    <div class="mx-1">
                        <icon-button
                            icon="Check"
                            color="white"
                            :on="on"
                            @click="onDone"
                        />
                    </div>
                </template>
                {{ $t("Mark done") }}
            </v-tooltip>
            <v-menu
                content-class="overflow-y-hidden dropmenu"
                bottom
                offset-y
                left
            >
                <template v-slot:activator="{ on }">
                    <div class="mx-1">
                        <icon-button
                            color="white"
                            :on="on"
                            icon="MoreVertical"
                        />
                    </div>

                </template>
                <v-list dense>
                    <v-list-item
                        class="menu-list-item"
                        style="height: 32px !important; min-height: 32px !important"
                        @click="closeBulkSelection"
                    >
                        <v-list-item-icon>
                            <icon icon="CheckCheck" />
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ $t("Hide multiselect") }}
                            </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action v-if="textForOS !== ''">
                            <v-chip
                                label
                                class="tag"
                            >
                                {{ textForOS }}
                            </v-chip>
                        </v-list-item-action>
                    </v-list-item>
                    <v-list-item
                        class="menu-list-item"
                        style="height: 32px !important; min-height: 32px !important"
                        @click="deleteTask"
                    >
                        <v-list-item-icon>
                            <icon icon="Trash2" />
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ $t("Delete task(s)") }}
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </v-menu>
        </template>
    </BulkActionBar>
</template>

<script>
import {
    ref, watch, onMounted, computed
} from "@vue/composition-api";
import { $t } from "@/i18n";
import BulkActionBar from "../BulkActionBar";
import DelegateMenu from "../BulkActionDelegate";
import BulkTaskSnoozeMenu from "../BulkTaskSnoozeMenu";
import TaskSnoozeMenu from "@/views/Task/TaskSnoozeMenu";
import SplitTimeMenu from "../SplitTimeMenu";
import TaskModule from "../../store/modules/task";
import AppModule from "../../store/modules/app";
import DialogModule from "../../store/modules/dialog";
import AccountModule from "../../store/modules/account";

export default {
    name: "BulkActionBarDashBoard",
    components: {
        BulkActionBar,
        DelegateMenu,
        BulkTaskSnoozeMenu,
        SplitTimeMenu
    },
    props: {
        items: {
            type: Array,
            default: null
        },
        isMobile: {
            type: Boolean,
            default: false
        },
        selectedListIds: {
            type: Array,
            default: () => []
        },
        showBulkSelect: {
            type: Boolean,
            default: true
        },
        relativeElem: {
            type: Object,
            default: null
        }
    },
    setup(props) {
        const {
            clearAllSelectedListIds,
            setExtraWork,
            setImportant,
            setMarkDone,
            markDoneErrorsTotal,
            deleteTasksErrors,
            bulkActionErrorsTotal,
            saveTaskTimeEntriesErrorsTotal
        } = TaskModule();
        const {
            textForOS,
            isCalendarOpen,
            setTextBasedOnOS
        } = AppModule();
        const {
            openDialog,
            setDialogOptions,
        } = DialogModule();
        const {
            current_account,
            available_company_accounts
        } = AccountModule();

        const isExtraWork = ref(false);
        const isImportant = ref(false);

        function toggleExtraWork() {
            isExtraWork.value = !isExtraWork.value;
        }

        function onExtraWork() {
            toggleExtraWork();
            setExtraWork(isExtraWork.value);
        }

        function toggleSetImportant() {
            isImportant.value = !isImportant.value;
        }

        function onSetImportant() {
            toggleSetImportant();
            setImportant(isImportant.value);
        }

        async function onDone() {
            await setMarkDone();
        }

        const splitTimeMenuIsBillable = computed(() => {
            const companyIds = props.items
                .filter(x => props.selectedListIds.includes(x.id))
                .map(x => x.account_id)

            const hasTimeBillableDisabled = available_company_accounts.value.some(x => companyIds.includes(x.id) && x.billing_settings && x.billing_settings.time_billable === 0)
            return !hasTimeBillableDisabled
        })

        function deleteTask() {
            let dialog_props = {
                component: () => import("@/views/Dialog/WarningDialog"),
                payload: {
                    type: "dashboard",
                    title: $t("Delete") + "?",
                    description: $t("All of the data related to the task will be deleted")
                }
            };

            let includesOtherAssignees = props.items.some(item =>
                props.selectedListIds.includes(item.id) &&
                (item.assignees.includes(current_account.value.id) ? item.assignees.length > 1 : true)
            );

            let trackedTime = props.items.some(item =>
                item.tracked_time && props.selectedListIds.includes(item.id) && !!Object.keys(item.tracked_time).length > 0
            );

            if (includesOtherAssignees) {
                dialog_props.payload.description = $t("Some tasks include other people. Are you sure you want to delete?");
            }
            if (trackedTime) {
                dialog_props.payload.description = $t("Time has been tracked to some of the selected activities. Are you sure you want to delete?");
            }
            if (includesOtherAssignees && trackedTime) {
                dialog_props.payload.description = $t("Time has been tracked to some of the selected activities. Some tasks include other people. Are you sure you want to delete?");
            }

            setDialogOptions(dialog_props);
            openDialog();
        }

        const closeBulkSelection = () => {
            clearAllSelectedListIds();
        };

        watch (
            () => markDoneErrorsTotal.value,
            () => {
                if (markDoneErrorsTotal.value > 0) {
                    setDialogOptions({
                        component: () => import("@/views/Dialog/WarningDialog"),
                        payload: {
                            type: "dashboard",
                            title: `${props.selectedListIds.length} ${$t("task(s) were not marked done")}`,
                            // CODE REVIEW WITH JAANUS  - title: `${selectedListIds.value.length / 2} ${$t("task(s) were not marked done")}`,
                            description: $t(
                                "Please check if the checklist has been completed or time is entered and they are assigned to you"
                            ),
                            hasOneButton: true
                        }
                    });
                    openDialog();
                }
            }, { immediate: true }
        );

        watch (
            () => saveTaskTimeEntriesErrorsTotal.value,
            () => {
                if (saveTaskTimeEntriesErrorsTotal.value > 0) {
                    setDialogOptions({
                        component: () => import("@/views/Dialog/WarningDialog"),
                        payload: {
                            type: "dashboard",
                            title: `${props.selectedListIds.length} ${$t("task(s) time was not updated")}`,
                            description: $t(
                                "Please make sure that you can add time to task"
                            ),
                            hasOneButton: true
                        }
                    });
                    openDialog();
                }
            }, { immediate: true }
        );

        watch (
            () => deleteTasksErrors.value,
            () => {
                if (deleteTasksErrors.value.length > null) {
                    setDialogOptions({
                        component: () => import("@/views/Dialog/WarningDialog"),
                        payload: {
                            type: "dashboard",
                            title: `${props.selectedListIds.length} ${$t("task(s) were not deleted")}`,
                            // CODE REVIEW WITH JAANUS - title: `${selectedListIds.value.length / 2} ${$t("task(s) were not deleted")}`,
                            description: deleteTasksErrors.value.join(', '),
                            hasOneButton: true
                        }
                    });
                    openDialog();
                }
            }, { immediate: true }
        );

        onMounted(() => {
            setTextBasedOnOS();
        });

        return {
            onExtraWork,
            onSetImportant,
            onDone,
            deleteTask,
            closeBulkSelection,
            textForOS,
            isCalendarOpen,
            bulkActionErrorsTotal,
            splitTimeMenuIsBillable
        }
    }
}
</script>